import { AxiosRequestConfig, AxiosResponse } from "axios";
import AxiosActions, { requestWithProgress } from "../../AxiosActions";
import { getAxiosInstances } from "../../../../utils/AppUtils";
import { OrderOutcome } from "../../../../utils/appEnums/AppEnums";
import { TListApiResponse } from "../commonTypes";
import { uatSearchParamRegex } from "../../../../validators/validatorConstants";

export const updatePatientInsurance = (
    orderId: number,
    insuranceId: number,
    patientId: number,
    dto: any,
    showSpinner: boolean,
    config?,
): Promise<AxiosResponse> => {
    return AxiosActions.put(getAxiosInstances().V2, `/health/order/${orderId}/patient/${patientId}/insurance/${insuranceId}`, dto, { ...config }, showSpinner);
};

export const getMasterOrderDetails = (id: number, config) => {
    return AxiosActions.get(getAxiosInstances().V2, `/health/master-order/${id}/details`, { ...config }, true);
};

export const createOrder = (dto: any): Promise<AxiosResponse> => {
    return AxiosActions.post(getAxiosInstances().V2, `/health/order`, dto, { withCredentials: true }, true);
};

export const makeEcommerceOrder = (dto: any): Promise<AxiosResponse> => {
    return AxiosActions.post(getAxiosInstances().V2, `/health/order/e-commerce`, dto, {}, true);
};

export const upsertDataResult = (orderId: string, dto: any): Promise<AxiosResponse> => {
    return AxiosActions.post(getAxiosInstances().V2, `/health/order/${orderId}/test-result`, dto, {}, true);
};

export const upsertFileResult = (orderId: string, file: File, resultName: string): Promise<AxiosResponse> => {
    const formData = new FormData();
    formData.append("attachment", file);
    formData.append("resultName", resultName);

    return AxiosActions.post(getAxiosInstances().V2, `/health/order/${orderId}/test-result/file`, formData, {}, true);
};

export const getOrderJourney = (orderId: string, config?: any): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, `/health/order/${orderId}/journey`, { ...config }, true).then((res) => {
        res.data.type = "Order";
        return res;
    });
};

export const getWorkflowJourney = (workflowId: string, config?: any): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, `/journey/${workflowId}`, { ...config }, true); // воркфловид
};

export const setDueDate = (
    workflowId: string | number,
    stepId: string | number,
    dto: { dueDate: Date | string },
    config: AxiosRequestConfig = {},
    spinner = true,
): Promise<AxiosResponse> => {
    return AxiosActions.put(getAxiosInstances().V2, `/journey/${workflowId}/step/${stepId}`, dto, config, spinner);
};

export const getOrderById = (orderId: string | number, config?: any): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, `/health/order/${orderId}/full-template`, { ...config }, true);
};

export const getOrderDetails = (orderId: string | number, config?: any): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, `/health/order/${orderId}/details`, { ...config }, true);
};

export const getOrderStepDataById = (orderId: string | number, stepId: string | number, config?: any): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, `/health/order/${orderId}/step/${stepId}/info`, { ...config }, true);
};

export const getCampaignStepDataById = (journeyId: string | number, stepId: string | number, config?: any): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, `/journey/${journeyId}/step/${stepId}/info`, { ...config }, true);
};

export const downloadTrackedFile = (journeyId, stepId, fileId: number, config: AxiosRequestConfig = {}, spinner = true): Promise<AxiosResponse> => {
    return requestWithProgress(
        getAxiosInstances().V2,
        "get",
        `/journey/${journeyId}/step/${stepId}/file/${fileId}`,
        {},
        { ...config, responseType: "blob" },
        spinner,
    );
};

export const setOrderOutcome = (orderId: string | number, outcome: OrderOutcome, config?: any) => {
    return AxiosActions.put(getAxiosInstances().V2, `/health/order/${orderId}?outcome=${outcome}`, {}, { ...config }, true);
};

export const getPatientOrder = (config?: any, spinner = false): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, `/health/order/my-orders`, { ...config }, spinner);
};

export const getPrescribingPhysician = (
    orderId: number,
    stepId: number,
    size: number,
    page: number,
    searchText: string,
    spinner: boolean,
    config = {},
): Promise<AxiosResponse> => {
    return AxiosActions.get(
        getAxiosInstances().V2,
        `health/order/${orderId}/step/${stepId}/prescribing-physician`, // ?size=${size}&page=${page}&searchText=${searchText}
        {
            params: {
                size,
                page,
                searchText,
            },
            ...config,
        },
        spinner,
        false,
    );
};

export const getInsurancesByPatientId = (orderId: string | number, patientId: string | number, config?: any): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, `health/order/${orderId}/patient/${patientId}/insurance`, { ...config }, true);
};

export const createPatientInsurance = (orderId: number, patientId: number, dto: any, config?: any): Promise<AxiosResponse> => {
    return AxiosActions.post(getAxiosInstances().V2, `/health/order/${orderId}/patient/${patientId}/insurance`, dto, { ...config }, true);
};

/** @deprecated it is no longer imported */
export const updateOrderPatient = (orderId: number, personId: number, dto: any): Promise<AxiosResponse> => {
    return AxiosActions.put(getAxiosInstances().V2, `/health/order/${orderId}/patient/${personId}`, dto, { withCredentials: true }, true);
};

export const deleteOrderKits = (id: number, dto, config: any = {}, spinner = false): Promise<AxiosResponse> => {
    return AxiosActions.delete(getAxiosInstances().V2, `/health/order/${id}/kits`, { ...config, data: dto }, spinner);
};

export const getOrderDocuments = (orderId: string, config?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, `/health/order/${orderId}/documents`, { ...config }, true);
};

export const getRequisitionPDF = (id: number): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, `/pdf/order/${id}/requisition`, { responseType: "blob" }, true);
};

export const getTestResultPDF = (orderId: number): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, `/pdf/order/${orderId}/test-result`, { responseType: "blob" }, true);
};

export const getZipArchiveForOrder = (orderId: number): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, `/health/order/${orderId}/file/all/zip`, { responseType: "blob" }, true);
};

export const getStepInfoByKey = (orderId: number, stepKey: string, config: any = {}, spinner = false): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, `/health/order/${orderId}/step/${stepKey}`, config, spinner);
};

export const getCollectionSpecificationsByOrderId = (
    orderId: number,
    collectionSpecificationId: number,
    config: AxiosRequestConfig = {},
    spinner = true,
): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, `/health/order/${orderId}/collection-specification/${collectionSpecificationId}`, config, spinner);
};

export const getICD10CodeTree = (orderId: number, page: number, searchText: string, size: number, config: any = {}, spinner = true): Promise<AxiosResponse> => {
    return AxiosActions.get(
        getAxiosInstances().V2,
        `health/order/${orderId}/medical-code/icd10/tree`, // ?page=${page}&searchText=${searchText}&size=${size}
        {
            params: {
                page,
                size,
                searchText,
            },
            ...config,
        },
        spinner,
    );
};

export const updateWorkflowStep = (orderId: number, stepId: number, dto: any): Promise<AxiosResponse> => {
    return AxiosActions.post(getAxiosInstances().V2, `/health/order/${orderId}/step/${stepId}/submit`, dto, { withCredentials: true }, true);
};

export const updateCampaignWorkflowStep = (journeyId: number, stepId: number, dto: any): Promise<AxiosResponse> => {
    return AxiosActions.post(getAxiosInstances().V2, `/journey/${journeyId}/step/${stepId}/submit`, dto, { withCredentials: true }, true);
};

export const addFilesToWorkflowStep = (orderId: number, stepId: number, files?: any): Promise<AxiosResponse> => {
    const formData = new FormData();
    files.forEach((element, index) => {
        if (element?.file) {
            formData.append(`rawData[${index}].file`, element.file);
        }
        const data = JSON.stringify(element.data);
        formData.append(`rawData[${index}].data`, data);
    });
    return AxiosActions.post(getAxiosInstances().V2, `/health/order/${orderId}/step/${stepId}/submit?attachment`, formData, { withCredentials: true }, true);
};

export const getOrderMockedData = (config: AxiosRequestConfig = {}, spinner = true): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, "/health/order/mock/details", config, spinner);
};

export const updateOrderUATStatus = (dto: { idsToSet: number[]; idsToUnset: number[] }, config: AxiosRequestConfig, spinner = true): Promise<AxiosResponse> => {
    return AxiosActions.put(getAxiosInstances().V2, "/health/order/uat", dto, config, spinner);
};

enum DataDefinitionTypes {
    ORDER = "Order",
    CAMPAIGN = "Campaign",
    COHORT_DEFINITION = "Cohort Definition",
}

export const getOrderDataDefinition = (workflowType?: `${DataDefinitionTypes}`, config: AxiosRequestConfig = {}): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, `/health/order/data-definition/list`, { ...config, params: { workflowType } }, true);
};

/**
 * @param config supports query param {{orderType}}: possible values: "Not UAT", "UAT", "All"
 * @returns if queryParam {{orderType}} is undefined - return Non UAT Orders
 */
export const getOrdersV3 = (dto, config: AxiosRequestConfig, spinner = false): Promise<AxiosResponse> => {
    if (!config?.params?.filter) {
        return AxiosActions.post(getAxiosInstances().V3, `/health/grid/order`, dto, config, spinner);
    }

    if (!config.params.filter.includes("isUat")) {
        return AxiosActions.post(getAxiosInstances().V3, `/health/grid/order`, dto, config, spinner);
    }

    const shallowConfig = { ...config };

    try {
        const isUatSearch = shallowConfig.params.filter.match(uatSearchParamRegex).groups.searchValue;
        const isSecondUatSearch = shallowConfig.params.filter.match(uatSearchParamRegex).groups.secondValue;
        shallowConfig.params.filter = shallowConfig.params.filter.replace(uatSearchParamRegex, "");
        shallowConfig.params.orderType = "All";

        if (isUatSearch === "true") {
            shallowConfig.params.orderType = "UAT";
        }

        if (isUatSearch === "false") {
            shallowConfig.params.orderType = "NOT Uat";
        }

        if (isSecondUatSearch) {
            shallowConfig.params.orderType = "All";
        }
    } catch {
        return AxiosActions.post(getAxiosInstances().V3, `/health/grid/order`, dto, shallowConfig, spinner);
    }

    return AxiosActions.post(getAxiosInstances().V3, `/health/grid/order`, dto, shallowConfig, spinner);
};

export const getOrderSamples = (body, config: AxiosRequestConfig = {}, spinner = true): Promise<AxiosResponse> => {
    return AxiosActions.post(getAxiosInstances().V3, `/health/grid/sample`, body, config, spinner);
};

export const getOrderComments = (body, config: AxiosRequestConfig = {}, spinner = true): Promise<AxiosResponse> => {
    return AxiosActions.post(getAxiosInstances().V3, `/health/grid/comment`, body, config, spinner);
};

export const getAcademicTestScore = (orderId: number, wftStepId: number, dto: any): Promise<AxiosResponse> => {
    return AxiosActions.post(getAxiosInstances().V2, `/health/order/${orderId}/step/${wftStepId}/academic-test-score`, dto, { withCredentials: true }, true);
};

// campaign journey;

export const addFilesToJourneyCampaignStep = (journeyId: number, stepId: number, files?: any): Promise<AxiosResponse> => {
    const formData = new FormData();
    files.forEach((element, index) => {
        if (element?.file) {
            formData.append(`rawData[${index}].file`, element.file);
        }
        const data = JSON.stringify(element.data);
        formData.append(`rawData[${index}].data`, data);
    });
    return AxiosActions.post(getAxiosInstances().V2, `/journey/${journeyId}/step/${stepId}/submit?attachment`, formData, { withCredentials: true }, true);
};

// Works for Campaign and Order journeys alike — the BE resolves the journey type and routes to the
// matching submitter (NG-8515). Expects the journey id, never an order id.
export const submitCustomFormJourneyStep = (
    journeyId: number,
    stepId: number,
    dto: FormData,
    config: AxiosRequestConfig = {},
    spinner = true,
): Promise<AxiosResponse> => {
    return AxiosActions.post(
        getAxiosInstances().V2,
        `/journey/${journeyId}/step/${stepId}/submit?attachment`,
        dto,
        { ...config, withCredentials: true, params: { ...config.params } },
        spinner,
    );
};

export const getTrackedUrl = (journeyId: number, stepId: number, identifier: string): Promise<AxiosResponse<{ redirectUrl: string }>> => {
    return AxiosActions.get(getAxiosInstances().V2, `/journey/${journeyId}/step/${stepId}/identifier/${identifier}`, {}, true);
};

export type TWorkflowLog = {
    id: number;
    name: string;
    type: string;
    content: string;
    created: string;
    updated: string;
    byUser: {
        name: string;
        id: number;
        email: string;
        firstName: string;
        lastName: string;
        phoneNumber: string;
        phoneNumberRegion: string;
        birthDate: string;
    };
};

export const getWorkflowLogs = (
    instanceId: number,
    config: AxiosRequestConfig = {},
    spinner = true,
): Promise<AxiosResponse<TListApiResponse<TWorkflowLog>>> => {
    return AxiosActions.get(getAxiosInstances().V2, `/journey/${instanceId}/logs`, config, spinner);
};

export const getBrandedRequisitionFile = (orderId: number, file: File, config: AxiosRequestConfig = {}, spinner = true) => {
    const formData = new FormData();
    formData.append("pdfFile1", file);
    return AxiosActions.post(getAxiosInstances().V2, `/pdf/order/${orderId}/generate-branded-requisition`, formData, config, spinner);
};
